import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Inject,
  OnInit,
  Renderer2,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { HomepageComponent } from './homepage/homepage.component';
import { FooterComponent } from './footer/footer.component';
import { DOCUMENT } from '@angular/common';
import { GoogleAnalyticsService } from './Services/google-analytics.service';
import { ActivationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, FooterComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    @Inject(DOCUMENT) private document: Document,
    private meta: Meta,
  ) {
    this.meta.addTags([
      // General SEO Tags
      {
        name: 'description',
        content:
          'Bourne Bespoke - Creative Home Enhancement, specializing in custom home features including media walls, home offices, bars, wardrobes, and commercial spaces.',
      },
      {
        name: 'keywords',
        content:
          'luxury home enhancements, custom media walls, bespoke home offices, drinks bars, wardrobes, commercial spaces, UK craftsmanship, Bourne Bespoke',
      },
      { name: 'author', content: 'Bourne Bespoke' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },

      // Open Graph Tags
      {
        property: 'og:title',
        content: 'Bourne Bespoke - Creative Home Enhancement',
      },
      {
        property: 'og:description',
        content:
          'Excellence in every detail. Bourne Bespoke specializes in unique home enhancements, offering a perfect balance of form and function.',
      },
      {
        property: 'og:image',
        content: 'https://bournebespoke.com/assets/hi-res/hero.png',
      }, // Replace with actual image URL
      { property: 'og:url', content: 'https://bournebespoke.com/' },
      { property: 'og:type', content: 'website' },

      // Twitter Card Tags
      { name: 'twitter:card', content: 'summary_large_image' },
      {
        name: 'twitter:title',
        content: 'Bourne Bespoke - Creative Home Enhancement',
      },
      {
        name: 'twitter:description',
        content:
          'Explore custom luxury home enhancements by Bourne Bespoke, tailored to perfection for every space.',
      },
      {
        name: 'twitter:image',
        content: 'https://bournebespoke.com/assets/hi-res/hero.png',
      }, // Replace with actual image URL

      // Section-Specific Tags
      {
        name: 'description',
        content:
          'Bourne Bespoke - Creative Home Enhancement: Excellence in every detail. Specializing in custom home solutions for discerning clients.',
      },
      {
        name: 'keywords',
        content:
          'Bourne Bespoke, Creative Home Enhancement, luxury custom solutions',
      },

      // About Section
      {
        name: 'description',
        content:
          'Learn about Bourne Bespoke - a unique company specializing in home enhancement creations, designed and built by passionate craftsmen.',
      },
      {
        name: 'keywords',
        content:
          'about Bourne Bespoke, custom home enhancements, expert craftsmanship, property development',
      },

      // Creations Section
      {
        name: 'description',
        content:
          'Explore Bourne Bespoke’s creations: Media walls, home offices, drinks bars, wardrobes, and commercial spaces. Our five core categories define luxury living.',
      },
      {
        name: 'keywords',
        content:
          'custom media walls, bespoke home offices, luxury drinks bars, wardrobes, commercial spaces',
      },

      // Contact Banner
      {
        name: 'description',
        content:
          'Let’s talk about your vision. Contact Bourne Bespoke today for bespoke home enhancements tailored to your needs.',
      },
      {
        name: 'keywords',
        content:
          'contact Bourne Bespoke, custom home design, bespoke solutions, start your project',
      },

      // Craftsmanship and Footprint Section
      {
        name: 'description',
        content:
          'Bourne Bespoke delivers unparalleled craftsmanship across the UK. Discover our footprint and commitment to excellence in every project.',
      },
      {
        name: 'keywords',
        content:
          'UK craftsmanship, Bourne Bespoke footprint, luxury home projects, custom home enhancements',
      },

      // Footer (General Info)
      {
        name: 'keywords',
        content: 'Bourne Bespoke, privacy policy, social media, navigation',
      },
    ]);
  }

  router = inject(Router);
  googleAnalyticsService = inject(GoogleAnalyticsService);
  activatedRouteChange = this.router.events
    .pipe(takeUntilDestroyed())
    .pipe(filter((event) => event instanceof ActivationEnd))
    .subscribe((data) => {
      const { snapshot } = <ActivationEnd>data;
      const { url } = snapshot;
      this.googleAnalyticsService.createPageView(url.join('/'));
    });

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    this.checkScroll();
  }

  ngOnInit(): void {
    this.checkScroll();
  }

  checkScroll() {
    const elements: NodeListOf<HTMLElement> =
      this.document.querySelectorAll('.hidden');
    const elementArray = Array.from(elements);

    elementArray.forEach((element) => {
      if (this.isElementInViewport(element)) {
        this.renderer.removeClass(element, 'hidden');
        this.renderer.addClass(element, 'show');
      }
    });
  }

  isElementInViewport(el: HTMLElement) {
    const rect = el.getBoundingClientRect();
    return (
      rect.top <
        (window.innerHeight - 100 ||
          document.documentElement.clientHeight - 100) &&
      rect.bottom >= 0 &&
      rect.left >= 0 &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }
}
